<template>
  <div :class="getContainerClass()">
    <div class="v-input__label-wrapper">
      <label v-if="label">
        <slot name="label">
          {{ label }}
          <span v-if="required" class="v-input__required">(Required)</span>
        </slot>
      </label>

      <div class="hint-wrapper">
        <VHint
          v-if="hintProps"
          v-bind="hintProps"
        />
      </div>
    </div>

    <div class="input-outer">
      <div
        class="input-icon__wrapper"
        :class="{ ['hide-label']: hideLabel, [iconPosition]: true }"
      >
        <div
          v-if="icon"
          :class="{
            ['icon-wrapper']: true,
            ['focused']: isFocused,
            ['hovered']: hovered
          }"
        >
          <i :class="getIconClass()"></i>
        </div>

        <input
          :type="localType"
          :id="name"
          :name="name"
          :autocomplete="autocomplete"
          :placeholder="placeholder"
          :maxlength="maxlength || false"
          :value="value"
          :disabled="disabled"
          @input="emitValue"
          @keyup="emitKeyUp"
          @focus="onFocus"
          @blur="onBlur"
          @mouseover="hovered = true"
          @mouseleave="hovered = false"
          @focusin="$emit('focusin')"
          @focusout="$emit('focusout')"
        />

        <div v-if="type === 'password'" class="toggle-password-view">
          <i :class="getPasswordIconClass" @click="_togglePasswordHidden"></i>
        </div>
      </div>

      <div v-for="(error, idx) in errors" :key="idx" class="error">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import VHint from "@/components/VHint";

const TextInputProps = {
  placeholder: {
    type: String,
    default: ""
  },
  maxlength: Number,
  label: String,
  value: String,
  autocomplete: VueTypes.oneOf(["on", "off"]).def("on"),
  name: VueTypes.string.isRequired,
  errors: VueTypes.arrayOf(VueTypes.string).def([]),
  layout: VueTypes.oneOf(["full", "half"]).def("full"),

  // Font awesome icon name e.g for "fa fa-filter" use "filter"
  icon: VueTypes.string,
  iconFABase: VueTypes.oneOf(["far", "fal", "fas"]).def("far"),
  iconPosition: VueTypes.oneOf(["left", "right"]).def("left"),
  classType: VueTypes.oneOf([
    "primary",
    "secondary",
    "tertiary",
    "danger",
    "dark"
  ]).def("primary"),

  // hint text to be displayed next to label
  hint: VueTypes.string.def(""),

  // add additional padding so it can be at the same level
  // while using 'half' layout
  hideLabel: VueTypes.bool.def(false),
  type: VueTypes.oneOf(["text", "password"]).def("text"),
  disabled: VueTypes.bool.def(false),
  extraClass: VueTypes.string,
  required: VueTypes.bool.def(false),
  innerChildProps: VueTypes.object
};

export default Vue.component("VInput", {
  props: TextInputProps,

  components: {
    VHint
  },

  data() {
    return {
      isFocused: false,
      hovered: false,
      passwordHidden: true,

      localType: this.type
    };
  },

  methods: {
    _toggleFocused() {
      this.isFocused = !this.isFocused;
    },

    _togglePasswordHidden() {
      this.passwordHidden = !this.passwordHidden;
    },

    onBlur() {
      this._toggleFocused();
      this.$emit("blur");
    },

    onFocus() {
      this._toggleFocused();
      this.$emit("focus");
    },

    emitKeyUp(event) {
      this.$emit("keyup", event);
    },

    emitValue(event) {
      this.$emit("input", event.target.value);
    },

    getContainerClass() {
      return {
        ["input-container"]: true,
        [this.layout]: true,
        [this.classType]: true,
        [this.extraClass]: true,
        ["has-error"]: this.errors.length
      };
    },

    getIconClass() {
      return {
        [this.iconFABase]: true,
        [`fa-${this.icon}`]: true,
        [this.classType]: true
      };
    }
  },

  computed: {
    getPasswordIconClass() {
      return `fa fa-eye${this.passwordHidden ? "-slash" : ""}`;
    },

    hintProps() {
      return this.innerChildProps["hint"];
    }
  },

  watch: {
    passwordHidden: function(value) {
      this.localType = value ? "password" : "text";
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-input";
$input-height: 30px;

.#{$ns} {
  &__label-wrapper {
    display: flex;
    align-items: baseline;

    .hint-wrapper {
      margin-left: 10px;
    }
  }

  &__required {
    font-size: 15px;
    font-family: Lato-Light, sans-serif;
  }
}

.toggle-password-view {
  display: flex;
  align-items: center;
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  cursor: pointer;
  color: #b4b2b2;

  &:hover {
    i {
      color: $primary-color;
    }
  }
}

input {
  display: block;
  width: 100%;
  -webkit-appearance: none;
  color: #1f1f1f;
  border-radius: 3px;
  font-size: 15px;
  padding: 10px;
  transition: border-color 0.2s linear;
  height: $input-height;
  border: 1px solid $primary-border-color;
  font-family: "Lato-Regular", sans-serif;

  &.disabled {
    background-color: #e6e6e6;
  }

  &:focus {
    outline: 0;
  }

  &::placeholder {
    font-family: "Lato-Light", sans-serif;
    font-style: italic;
    font-size: 15px;
    color: #979797;
  }
}

label {
  @include textCropEllipsis();
  font-size: 15px;
  color: $primary-text-color;
  font-family: "Lato-Bold", sans-serif;
  padding-bottom: 14px;
  padding-left: 8px;

  font-weight: 300;
  vertical-align: middle;
  display: inline-block;
  flex-shrink: 0;
}

.error {
  width: 100%;
  font-size: 14px;
  padding-top: 7px;
  font-family: Lato-Light, sans-serif;
  text-align: left;

  &:nth-child(1) {
    padding: 0.7em 0.1em;
  }

  &,
  &:focus {
    color: $danger-color;
  }
}

.full {
  width: 100%;
}

.half {
  display: inline-block;
  width: calc(50% - 1em);
}

.icon-wrapper {
  display: flex;
  align-items: center;
}

.primary {
  .icon-wrapper.focused,
  input {
    &:focus,
    &:hover {
      border-color: $primary-color;
    }
  }

  .icon-wrapper {
    &.focused i {
      color: $primary-color;
    }

    &.hovered,
    &.focused {
      border-color: $primary-color;
    }
  }
}

.secondary {
  .icon-wrapper.focused,
  input {
    &:focus,
    &:hover {
      border-color: $secondary-color;
    }
  }

  .icon-wrapper {
    &.focused i {
      color: $secondary-color;
    }

    &.hovered {
      border-color: $secondary-color;
    }
  }
}

.tertiary {
  .icon-wrapper.focused,
  input {
    &:focus,
    &:hover {
      border-color: $tertiary-color;
    }
  }

  .icon-wrapper {
    &.focused i {
      color: $tertiary-color;
    }

    &.hovered,
    &.focused {
      border-color: $tertiary-color;
    }
  }
}

.dark {
  .icon-wrapper.focused,
  input {
    &:focus,
    &:hover {
      border-color: $button-dark-color;
    }
  }

  .icon-wrapper {
    &.focused i {
      color: $button-dark-color;
    }

    &.hovered {
      border-color: $button-dark-color;
    }
  }
}

input:disabled {
  &,
  &:hover {
    background: transparent;
    border: 1px solid $disabled-color;
    cursor: not-allowed;
  }
}

.has-error {
  input {
    &,
    &:focus {
      border-color: $danger-color;
    }
    color: $danger-color;
  }

  .input-icon__wrapper {
    &.left,
    &.right {
      .icon-wrapper {
        &,
        &.focused {
          border-color: $danger-color;
        }
      }
    }

    i {
      &,
      &.focused {
        color: $danger-color;
      }
    }
  }
}

.input-icon__wrapper {
  position: relative;
  display: flex;
  height: $input-height;

  &.hide-label {
    margin-top: 2.485em;
  }

  &.left,
  &.right {
    .icon-wrapper {
      &.hovered,
      &.focused {
        border-color: $primary-color;
      }
    }
  }

  &.left {
    .icon-wrapper {
      border: 1px solid $primary-border-color;
      border-right: none;
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
      padding: 7px 0 7px 7px;
      transition: border-color 0.2s linear;

      & + input {
        border-left: none;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }

  &.right {
    flex-direction: row-reverse;

    .icon-wrapper {
      border: 1px solid $primary-border-color;
      border-left: none;
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
      padding: 7px 7px 7px 0;
      transition: border-color 0.2s linear;

      & + input {
        border-right: none;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  i {
    font-size: 18px;
    color: #c5c5c5;
    transition: color 0.2s linear;
  }
}

.input-outer {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
